import React, { useState, useRef } from 'react';
import { UploadCloud, File, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { documentService } from '@/services/documentService';
import { DocumentRecord, DocumentType, EntityType } from '@/types/database';
import { formatFileSize } from '@/lib/utils';

interface DocumentUploaderProps {
  entityType: EntityType;
  entityId: string;
  defaultDocumentType?: DocumentType;
  allowedDocumentTypes?: DocumentType[];
  onUploadSuccess?: (doc: DocumentRecord) => void;
  className?: string;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
];

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  entityType,
  entityId,
  defaultDocumentType = 'weighing_sheet',
  allowedDocumentTypes,
  onUploadSuccess,
  className,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<DocumentType>(defaultDocumentType);
  const [notes, setNotes] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    setError(null);
    setSuccess(false);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds 50MB limit (${formatFileSize(file.size)}).`);
      return false;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|pdf|xlsx|xls|csv)$/i)) {
      setError('Unsupported file format. Please upload PDF, JPG, PNG, WEBP, or Excel/CSV.');
      return false;
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const doc = await documentService.uploadDocument({
        file: selectedFile,
        fileName: selectedFile.name,
        entityType,
        entityId,
        documentType: docType,
        notes: notes.trim() || undefined,
      });

      setSuccess(true);
      setSelectedFile(null);
      setNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onUploadSuccess) onUploadSuccess(doc);
    } catch (err: any) {
      console.error('[DocumentUploader] Upload error:', err);
      setError(err.message || 'Failed to upload document.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`space-y-4 ${className || ''}`}>
      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-emerald-600 bg-emerald-50'
            : selectedFile
            ? 'border-emerald-500 bg-slate-50'
            : 'border-slate-300 hover:border-slate-400 bg-white'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx"
        />

        {selectedFile ? (
          <div className="flex items-center justify-between bg-white p-3 rounded-md border border-slate-200 text-left">
            <div className="flex items-center gap-3 truncate">
              <File className="w-8 h-8 text-emerald-700 flex-shrink-0" />
              <div className="truncate">
                <p className="text-sm font-semibold text-slate-800 truncate">{selectedFile.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="p-1 text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <UploadCloud className="w-10 h-10 text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-700">
              Drag & drop file here or <span className="text-emerald-700 underline">browse device</span>
            </p>
            <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, WEBP, or Excel up to 50MB</p>
          </div>
        )}
      </div>

      {/* Upload Details */}
      {selectedFile && (
        <div className="space-y-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Document Classification</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value as DocumentType)}
              className="w-full text-sm rounded-md border border-slate-300 bg-white p-2 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            >
              {(allowedDocumentTypes || [
                'weighing_sheet',
                'invoice',
                'delivery_receipt',
                'settlement_pdf',
                'contract',
                'lab_result',
                'gate_pass',
                'general',
              ]).map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Optional Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. SIDC weighing slip ticket #8849"
              className="w-full text-sm rounded-md border border-slate-300 bg-white p-2 focus:outline-none focus:ring-2 focus:ring-emerald-600"
            />
          </div>

          <Button
            onClick={handleUpload}
            disabled={isUploading}
            className="w-full flex items-center justify-center gap-2"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading to Secure Storage...
              </>
            ) : (
              <>
                <UploadCloud className="w-4 h-4" /> Upload Document
              </>
            )}
          </Button>
        </div>
      )}

      {/* Status messages */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 p-3 rounded-md border border-red-200">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 p-3 rounded-md border border-emerald-200">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>Document uploaded and attached successfully!</span>
        </div>
      )}
    </div>
  );
};
